import { type FormEvent, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff } from 'lucide-react';
import {
  Badge,
  type BadgeVariant,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@beamx/shared';
import { suppression, type SuppressionFilter, type SuppressionReason } from '../lib/api/suppression.js';

const PAGE_SIZE = 50;

const REASON_VARIANT: Record<SuppressionReason, BadgeVariant> = {
  HardBounce: 'destructive',
  InvalidAddress: 'destructive',
  SpamComplaint: 'warning',
  SoftBounceLimit: 'warning',
  UserUnsubscribed: 'default',
  ManualAdd: 'muted',
  Unknown: 'muted',
};

export function SuppressionPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<SuppressionFilter>({ take: PAGE_SIZE, skip: 0 });
  const [reason, setReason] = useState<'' | SuppressionReason>('');
  const [emailContains, setEmailContains] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('');

  const q = useQuery({
    queryKey: ['suppression', filter],
    queryFn: () => suppression.list(filter),
    placeholderData: keepPreviousData,
  });

  const add = useMutation({
    mutationFn: () => suppression.add(newEmail.trim(), newReason.trim() || undefined),
    onSuccess: () => {
      toast.success('Address suppressed');
      setNewEmail('');
      setNewReason('');
      void qc.invalidateQueries({ queryKey: ['suppression'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to add suppression'),
  });

  const remove = useMutation({
    mutationFn: (email: string) => suppression.remove(email),
    onSuccess: () => {
      toast.success('Removed from suppression list');
      void qc.invalidateQueries({ queryKey: ['suppression'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to remove'),
  });

  function onApply(e: FormEvent) {
    e.preventDefault();
    setFilter({ take: PAGE_SIZE, skip: 0, reason: reason || undefined, emailContains: emailContains.trim() || undefined });
  }

  const data = q.data;
  const canPrev = (data?.skip ?? 0) > 0;
  const canNext = data ? data.skip + data.take < data.totalCount : false;

  return (
    <>
      <PageHeader
        title="Suppression list"
        description="Addresses BeamX refuses to send to. Bounces, complaints, and unsubscribes land here automatically."
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Add manually</CardTitle>
          <CardDescription>Recorded with reason ManualAdd; your note is stored on the entry.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
            className="flex flex-wrap gap-2"
          >
            <Input className="min-w-[240px] flex-1" type="email" placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required aria-label="Email" />
            <Input className="min-w-[240px] flex-1" placeholder="reason / note (optional)" maxLength={500} value={newReason} onChange={(e) => setNewReason(e.target.value)} aria-label="Reason" />
            <Button type="submit" loading={add.isPending}>Add</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <form onSubmit={onApply} className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="f-reason">Reason</Label>
              <Select id="f-reason" value={reason} onChange={(e) => setReason(e.target.value as '' | SuppressionReason)}>
                <option value="">Any reason</option>
                <option value="HardBounce">Hard bounce</option>
                <option value="SpamComplaint">Spam complaint</option>
                <option value="UserUnsubscribed">User unsubscribed</option>
                <option value="ManualAdd">Manually added</option>
                <option value="SoftBounceLimit">Soft-bounce limit</option>
                <option value="InvalidAddress">Invalid address</option>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="f-email">Email contains</Label>
              <div className="flex gap-2">
                <Input id="f-email" className="flex-1" value={emailContains} onChange={(e) => setEmailContains(e.target.value)} placeholder="@example.com" />
                <Button type="submit">Apply</Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="space-y-3 p-6">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : data && data.entries.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.entries.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs text-foreground">{s.email}</TableCell>
                      <TableCell><Badge variant={REASON_VARIANT[s.reason]}>{s.reason}</Badge></TableCell>
                      <TableCell className="max-w-md truncate text-xs text-muted-foreground" title={s.sourceDetail ?? ''}>{s.sourceDetail ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{new Date(s.createdAt).toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => remove.mutate(s.email)}>
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-muted-foreground">
                <span>{data.totalCount === 0 ? 0 : data.skip + 1}–{Math.min(data.skip + data.take, data.totalCount)} of {data.totalCount}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={!canPrev} onClick={() => setFilter((f) => ({ ...f, skip: Math.max(0, (f.skip ?? 0) - PAGE_SIZE) }))}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={!canNext} onClick={() => setFilter((f) => ({ ...f, skip: (f.skip ?? 0) + PAGE_SIZE }))}>Next</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-6">
              <EmptyState icon={ShieldOff} title="Nothing suppressed" description="Auto-suppressions appear here as receivers respond." />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
